// OBT — Bölge Mühendisi paneli (F1 iskeleti + F2-A kampanya entegrasyonu)
import { useState } from 'react';
import {
  ClipboardList,
  Search,
  Edit3,
  TreePine,
  Send,
  Megaphone,
  Inbox,
  BarChart3,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ObtDashboardFrame from '../ObtDashboardFrame';
import { useObtOverview, sumCampaignCounter } from '../useObtOverview';
import CampaignScreen from '../campaign/CampaignScreen';
import ReviewerWorkspace from '../review/ReviewerWorkspace';
import DistributeScreen from '../response/DistributeScreen';
import ObtReportsScreen from '../report/ObtReportsScreen';
import ObtMemoryScreen from '../memory/ObtMemoryScreen';
import ObtNotificationsPanel from '../notifications/ObtNotificationsPanel';
import ObtNotificationBell from '../notifications/ObtNotificationBell';

const TASK_CARDS = [
  {
    key: 'pending-review',
    title: 'İnceleme Bekleyen Cevaplar',
    description:
      'Formenlerinizin gönderdiği cevapları inceleyin. “Cevapları İncele” ile açın.',
    icon: Search,
  },
  {
    key: 'revisions',
    title: 'Revizyon Talepleri',
    description: 'İncelemede revizyon isteyin; cevap Formene geri gönderilir.',
    icon: Edit3,
  },
  {
    key: 'suggested-parks',
    title: 'Önerilen Parklar',
    description: 'Filtre dışı önerilen parkları onaylayın veya reddedin.',
    icon: TreePine,
  },
  {
    key: 'sent-up',
    title: 'Üst Makama Gönderilen Sonuçlar',
    description: 'Onayladığınız cevaplar Müdür Yardımcısına iletilir.',
    icon: Send,
  },
];

export default function ObtRegionEngineerDashboard({ enabled = true }) {
  const [view, setView] = useState('overview');
  const { loading, error, campaigns, page, queueItems, reload } = useObtOverview(
    'engineer',
    { enabled: enabled && view === 'overview' }
  );

  if (view === 'campaigns') {
    return (
      <CampaignScreen enabled={enabled} onBack={() => setView('overview')} />
    );
  }

  if (view === 'review') {
    return (
      <ReviewerWorkspace
        role="engineer"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'distribute') {
    return (
      <DistributeScreen enabled={enabled} onBack={() => setView('overview')} />
    );
  }

  if (view === 'reports') {
    return (
      <ObtReportsScreen
        role="engineer"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'memory') {
    return (
      <ObtMemoryScreen
        role="engineer"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'notifications') {
    return (
      <ObtNotificationsPanel
        role="engineer"
        enabled={enabled}
        onNavigate={(v) => setView(v)}
        onBack={() => setView('overview')}
      />
    );
  }

  const stats = [
    {
      key: 'queue',
      label: 'İncelememi bekleyen cevap',
      value: queueItems ? queueItems.length : 0,
      icon: Search,
      tone: 'warning',
      onClick: () => setView('review'),
    },
    {
      key: 'total',
      label: 'Kapsamımdaki bilgi talebi',
      value: page?.total ?? campaigns.length,
      icon: ClipboardList,
      tone: 'info',
      onClick: () => setView('campaigns'),
    },
    {
      key: 'submitted',
      label: 'Gönderilen cevap',
      value: sumCampaignCounter(campaigns, 'submitted_response_count'),
      icon: Send,
      tone: 'info',
      onClick: () => setView('reports'),
    },
    {
      key: 'late',
      label: 'Süresi geçen kişi',
      value: sumCampaignCounter(campaigns, 'late_person_count'),
      icon: Edit3,
      tone: 'danger',
      onClick: () => setView('reports'),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <ObtNotificationBell
          enabled={enabled && view === 'overview'}
          onClick={() => setView('notifications')}
        />
        <Button variant="outline" onClick={() => setView('reports')}>
          <BarChart3 className="mr-2 h-4 w-4" aria-hidden="true" />
          Raporlar
        </Button>
        <Button variant="outline" onClick={() => setView('memory')}>
          <Archive className="mr-2 h-4 w-4" aria-hidden="true" />
          Kurumsal Hafıza
        </Button>
        <Button variant="outline" onClick={() => setView('distribute')}>
          <Send className="mr-2 h-4 w-4" aria-hidden="true" />
          Dağıtım Görevleri
        </Button>
        <Button variant="outline" onClick={() => setView('review')}>
          <Inbox className="mr-2 h-4 w-4" aria-hidden="true" />
          Cevapları İncele
        </Button>
        <Button onClick={() => setView('campaigns')}>
          <Megaphone className="mr-2 h-4 w-4" aria-hidden="true" />
          Bilgi Taleplerini Yönet
        </Button>
      </div>
      <ObtDashboardFrame
        title="Operasyonel Bilgi Toplama"
        description="Formenlerinize yöneltilen bilgi taleplerinin takibi ve cevapların incelenmesi."
        stats={stats}
        taskCards={TASK_CARDS}
        campaignsTitle="Alt Zincirimdeki Bilgi Talepleri"
        campaignsEmptyText="Kapsamınızda bilgi talebi bulunmuyor"
        campaigns={campaigns}
        loading={loading}
        error={error}
        onReload={reload}
        onCampaignClick={() => setView('campaigns')}
      />
    </div>
  );
}
